import {
  IPluginOptions,
  IPluginOptionsPartial,
  LambdaStage,
  DefaultTemplateLangsSchema,
} from "@/schemas/pluginOptions/pluginOptionsSchema";
import { IMergeTagArray } from "@/schemas/mergeTags/mergeTagsSchema";
import setTheme from "./ThemeSetter";
import { loadLanguageAsync, i18n } from "./i18n-setup";
import { loadStyle } from "@/utils/loadStyle";
import { setApi, setLambdaStage } from "../api";
import ISavedBlock from "types/SavedBlock/ISavedBlock";
import INotification from "types/Notification/INotification";
import useApiAuthorizationHeader from "@/composables/useApiAuthorizationHeader";
import axios from "axios";

import { useNotificationsStore } from "@/stores/notifications";
import { useFontStore } from "@/stores/fonts";
import { useRolesStore } from "@/stores/roles";
import { useTopBarStore } from "@/stores/topBar";
import { useAutosavesStore } from "@/stores/autosaves";
import { useEcmStore } from "@/stores/ecm";
import { useMergeTagsStore } from "@/stores/mergeTags";
import { useFileManagerStore } from "@/stores/fileManger";
import { useTinyConfigStore } from "@/stores/tinyConfig";
import { useTiptapConfigStore } from "@/stores/tiptapConfig";
import { useBetaFeaturesStore } from "@/stores/betaFeatures";
import { useColorsStore } from "@/stores/colors";
import { useContentBlocksOptionsStore } from "@/stores/contentBlockOptions";
import { useApiKeysStore } from "@/stores/apiKeyStore";
import { useActiveMembersStore } from "@/stores/activeMembers";
import { useLayoutStore } from "@/stores/layoutStore";
import { useGeneralStore } from "@/stores/generalStore";
import { useMjmlStore } from "@/stores/mjmlStore";
import { useUserStore } from "@/stores/userStore";
import { useSavedBlocksStore } from "@/stores/savedBlocksStore";
import { usePremadeBlocksStore } from "@/stores/premadeBlocksStore";
import { useCustomBlocksStore } from "@/stores/customBlocksStore";
import { usePremadeTemplatesStore } from "@/stores/premadeTemplatesStore";
import { useGooglePromotionalStore } from "@/stores/googlePromotionalStore";
import { useOpenAiStore } from "@/stores/openAiStore";
import { useSectionVariantsStore } from "@/stores/sectionVariantsStore";
import { useChatAI } from "@/stores/chatAIStore";
import { usePreviewStore } from "@/stores/preview";
import { useCommentsStore } from "@/stores/commentsStore";
import { applyDefaultTemplateSettings } from "./applyDefaultTemplateSettings";
import { useApiAuthHeaderStore } from "@/stores/apiAuthHeaderStore";
import { useApiBlocksStore } from "@/stores/apiBlocks";
import { useHelpdeskStore } from "@/stores/helpdeskStore";
import { useDefaultsStore } from "@/stores/defaultsStore";
import { useImageEditorStore } from "@/stores/imageEditorStore";
import { withBusinessAndUp, withExpansionAndUp } from "@/utils/planGating";

type mode = "production" | "testing" | "development";

const setInitState = async (
  options: IPluginOptions,
  hostname: string,
  mode: mode
) => {
  if (!options.authorize) {
    throw new Error(
      "Object 'authorize' must be defined in options. Please attach your licence information"
    );
  }

  const activeMembersStore = useActiveMembersStore();
  const mjmlStore = useMjmlStore();

  axios.interceptors.request.use(
    function (config) {
      return config;
    },
    function (error) {
      const message = {
        type: "callback",
        action: "onError",
        data: {
          args: [
            "network",
            error.message || "Network error occurred.",
            error.response?.data,
          ],
        },
      };
      window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");

      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      const message = {
        type: "callback",
        action: "onError",
        data: {
          args: [
            "network",
            error.message || "Network error occurred.",
            error.response?.data,
          ],
        },
      };
      window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");
      return Promise.reject(error);
    }
  );

  mjmlStore.authorize = {
    hostname: hostname,
    apiKey: options.authorize.apiKey,
    userId: options.authorize.userId,
  };
  if (
    import.meta.env.VITE_APP_PREVIEW_APP &&
    import.meta.env.VITE_APP_PREVIEW_APP_LAMBDA_STAGE
  ) {
    setLambdaStage(
      import.meta.env.VITE_APP_PREVIEW_APP_LAMBDA_STAGE as LambdaStage
    );
  } else if (options.lambdaStage) {
    switch (options.lambdaStage) {
      case "local":
        setLambdaStage("local");
        break;
      case "stage1":
        setLambdaStage("stage1");
        break;
      case "stage2":
        setLambdaStage("stage2");
        break;
      case "stage3":
        setLambdaStage("stage3");
        break;
      default:
        setLambdaStage("production");
    }
  }

  try {
    await mjmlStore.authorizePromise({ options: options });
  } catch (e) {
    const message = {
      type: "callback",
      action: "onError",
      data: {
        args: [
          "authorize",
          "Unable to authorize, please check your licence information.",
        ],
      },
    };

    window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");
    throw new Error(
      `Unable to authorize, please check your licence information. + ${e}`
    );
  }

  if (!options.language) {
    const generalStore = useGeneralStore();
    await loadLanguageAsync(i18n, "en", options.textOverride);
    generalStore.language = "en";
  }

  await setOptions(options, mode, true);

  if (mode !== "production") {
    //@ts-expect-error
    window.TopolPlugin = {};

    window.TopolPlugin.load = (json: JSON) => {
      window.$event.emit("load", { json: json });
    };
    window.TopolPlugin.togglePreview = () => {
      window.$event.emit("togglePreview");
    };
    window.TopolPlugin.togglePreviewSize = () => {
      window.$event.emit("togglePreviewSize");
    };
    window.TopolPlugin.changeEmailToMobile = () => {
      mjmlStore.setIsMobileView(true);
    };
    window.TopolPlugin.changeEmailToDesktop = () => {
      mjmlStore.setIsMobileView(false);
    };
    window.TopolPlugin.chooseFile = (url: string) => {
      window.$event.emit("chooseFile", url);
    };
    window.TopolPlugin.save = (lang?: string) => {
      window.$event.emit("save", lang);
    };
    window.TopolPlugin.redo = () => {
      window.$event.emit("redo");
    };
    window.TopolPlugin.undo = () => {
      window.$event.emit("undo");
    };
    window.TopolPlugin.setSavedBlocks = (savedBlocks: Array<ISavedBlock>) => {
      window.$event.emit("setSavedBlocks", savedBlocks);
    };
    window.TopolPlugin.setPreviewHTML = (html: unknown) => {
      window.$event.emit("setPreviewHTML", html);
    };
    window.TopolPlugin.createNotification = (notification: INotification) => {
      window.$event.emit("createNotification", {
        ...notification,
        extarnalUse: true,
      });
    };
    window.TopolPlugin.setActiveMembers = (setActiveMembers: string[]) => {
      activeMembersStore.setActiveMembers(setActiveMembers);
    };
    window.TopolPlugin.toggleBlocksAndStructuresVisibility = () => {
      mjmlStore.blurHiddenElements = !mjmlStore.blurHiddenElements;
    };

    window.TopolPlugin.updateCustomBlockContent = () => {
      // TODO:implement
    };

    window.TopolPlugin.refreshComments = (key: string) => {
      window.$event.emit("refreshComments", key);
    };

    window.TopolPlugin.refreshSyncedSections = () => {
      window.$event.emit("refreshSyncedSections");
    };

    window.TopolPlugin.openPremadeTemplatesSelection = () => {
      window.$event.emit("openPremadeTemplatesSelection");
    };

    window.TopolPlugin.updateApiAuthorizationHeader = (
      newHeader: string | Record<string, string>
    ) => {
      window.$event.emit("updateApiAuthorizationHeader", newHeader);
    };
    window.TopolPlugin.setTemplateName = (name: string) => {
      window.$event.emit("setTemplateName", name);
    };
    window.TopolPlugin.updateOptions = (options: IPluginOptionsPartial) => {
      window.$event.emit("updateOptions", options);
    };
    window.TopolPlugin.setMergeTags = (mergeTags: IMergeTagArray) => {
      window.$event.emit("setMergeTags", mergeTags);
    };
    window.TopolPlugin.toggleChatAI = () => {
      window.$event.emit("toggleChatAI");
    };
    window.TopolPlugin.toggleAutosaves = () => {
      window.$event.emit("toggleAutosaves");
    };
    window.TopolPlugin.toggleComments = () => {
      window.$event.emit("toggleComments");
    };
    window.TopolPlugin.createLanguage = (lang: string) => {
      window.$event.emit("createLanguage", lang);
    };
    window.TopolPlugin.setPrimaryLanguage = (lang: string) => {
      window.$event.emit("setPrimaryLanguage", lang);
    };
    window.TopolPlugin.deleteLanguage = (lang: string) => {
      window.$event.emit("deleteLanguage", lang);
    };
    window.TopolPlugin.selectLanguage = (lang: string) => {
      window.$event.emit("selectLanguage", lang);
    };
    window.TopolPlugin.translateLanguage = (
      lang: string,
      sourceLang?: string
    ) => {
      window.$event.emit("translateLanguage", { lang, sourceLang });
    };
    window.TopolPlugin.translateAllLanguages = () => {
      window.$event.emit("translateAllLanguages");
    };
    window.TopolPlugin.getMutations = () => {
      window.$event.emit("getMutations");
    };
    window.TopolPlugin.toggleControlPanel = () => {
      window.$event.emit("toggleControlPanel");
    };
    window.TopolPlugin.updateTemplate = (
      json: JSON,
      options?: { skipSnapshot?: boolean }
    ) => {
      window.$event.emit("updateTemplate", { json, options });
    };
    window.TopolPlugin.toggleDarkMode = () => {
      window.$event.emit("toggleDarkMode");
    };
  }
};

export const setOptions = async (
  options: IPluginOptionsPartial,
  mode = "production",
  initial = false
) => {
  const notificationsStore = useNotificationsStore();
  const fontStore = useFontStore();
  const rulesStore = useRolesStore();
  const topBarStore = useTopBarStore();
  const autosavesStore = useAutosavesStore();
  const ecmStore = useEcmStore();
  const mergeTagsStore = useMergeTagsStore();
  const fileManagerStore = useFileManagerStore();
  const tinyConfigStore = useTinyConfigStore();
  const tiptapConfigStore = useTiptapConfigStore();
  const betaFeaturesStore = useBetaFeaturesStore();
  const colorsStore = useColorsStore();
  const contentBlocksOptionsStore = useContentBlocksOptionsStore();
  const apiKeyStore = useApiKeysStore();
  const layoutStore = useLayoutStore();
  const generalStore = useGeneralStore();
  const mjmlStore = useMjmlStore();
  const userStore = useUserStore();
  const savedBlocksStore = useSavedBlocksStore();
  const premadeBlocksStore = usePremadeBlocksStore();
  const customBlocksStore = useCustomBlocksStore();
  const premadeTemplatesStore = usePremadeTemplatesStore();
  const googlePromotionalStore = useGooglePromotionalStore();
  const openAiStore = useOpenAiStore();
  const sectionVariantsStore = useSectionVariantsStore();
  const chatAIStore = useChatAI();
  const previewStore = usePreviewStore();
  const commentsStore = useCommentsStore();
  const apiAuthHeaderStore = useApiAuthHeaderStore();
  const apiBlocksStore = useApiBlocksStore();
  const defaultsStore = useDefaultsStore();
  const helpdeskStore = useHelpdeskStore();
  const imageEditorStore = useImageEditorStore();
  const { setApiAuthorizationHeader } = useApiAuthorizationHeader();

  if (options.currentUser) {
    userStore.setCurrentUser(options.currentUser);
  }
  if (options.teamUsers) {
    userStore.setTeamUsers(options.teamUsers);
  }
  if (options.language) {
    await loadLanguageAsync(i18n, options.language, options.textOverride);
    generalStore.language = options.language;
  }
  apiBlocksStore.setProductBlock();
  if (options.api) {
    for (const [key, value] of Object.entries(options.api)) {
      const apiKey = key as keyof typeof options.api;

      if (
        apiKey === "FOLDERS" ||
        apiKey === "IMAGE_UPLOAD" ||
        apiKey === "IMAGE_EDITOR_UPLOAD"
      ) {
        const setApiGated = withBusinessAndUp("Self-hosted storage", () => {
          setApi(apiKey, value);
        });
        setApiGated();
      } else {
        setApi(apiKey, value);
      }

      if (
        apiKey === "FEEDS" ||
        apiKey === "PRODUCTS" ||
        apiKey === "PRODUCT_CATEGORIES"
      ) {
        apiBlocksStore.setProductsApi(apiKey, value);
      }
    }
  }
  if (options.light !== undefined) {
    generalStore.light = options.light;
  }
  if (options.textEditor !== undefined) {
    generalStore.textEditor = options.textEditor;
  }
  if (options.textEditorToolbarBottomPosition !== undefined) {
    generalStore.textEditorToolbarBottomPosition =
      options.textEditorToolbarBottomPosition;
  }
  if (options.premadeBlocks !== undefined) {
    premadeBlocksStore.setPremadeBlocks(options.premadeBlocks);
  }
  if (options.savedBlocks !== undefined) {
    savedBlocksStore.setSavedBlocks(options.savedBlocks);
  }
  if (options.syncedSectionsEnabled !== undefined) {
    savedBlocksStore.syncedSectionsEnabled = options.syncedSectionsEnabled;
  }
  if (options.mainMenuAlign) {
    layoutStore.mainMenuAlign = options.mainMenuAlign;
  }
  if (options.disableAlerts) {
    if (options.disableAlerts === true) {
      notificationsStore.disableNotifications();
    }
  }
  if (typeof options.hideSettingsTab === "boolean") {
    layoutStore.hideSettingsTab = options.hideSettingsTab;
  }
  if (typeof options.hideControlPanel === "boolean") {
    layoutStore.hideControlPanel = options.hideControlPanel;
  }
  if (typeof options.showControlPanelExpand === "boolean") {
    layoutStore.showControlPanelExpand = options.showControlPanelExpand;
  }
  if (typeof options.enableSubjectLine === "boolean") {
    layoutStore.enableSubjectLine = options.enableSubjectLine;
  }
  if (typeof options.aiLabeledElements === "boolean") {
    generalStore.aiLabeledElements = options.aiLabeledElements;
  }
  if (options.templateId) {
    // @ts-expect-error
    mjmlStore.templateId = options.templateId;
  }
  if (typeof options.removeTopBar === "boolean") {
    topBarStore.setRemoveTopBar(options.removeTopBar);
  }
  if (options.topBarOptions) {
    topBarStore.setVisibleTopBarElements(options.topBarOptions);
  }

  if (options.smartMergeTags) {
    mergeTagsStore.setSmartMergeTags(options.smartMergeTags);
  }

  if (options.enableMergeTagPreview !== undefined) {
    mergeTagsStore.setEnableMergeTagPreview(options.enableMergeTagPreview);
  }

  if (options.mergeTags) {
    mergeTagsStore.setMergeTags(options.mergeTags);
  }
  if (options.customFonts) {
    fontStore.setFonts(options.customFonts);
  }
  if (options.tinyConfig) {
    tinyConfigStore.setTinyConfig(options.tinyConfig);
  }
  if (options.tiptapConfig) {
    tiptapConfigStore.setTiptapConfig(options.tiptapConfig);
  }
  if (options.fontSizes) {
    fontStore.setFontSizes(options.fontSizes);
  }
  if (options.enableAutosaves !== undefined) {
    autosavesStore.enableAutosaves(options.enableAutosaves);
  }
  if (options.contentBlocks) {
    contentBlocksOptionsStore.setOptions(options.contentBlocks);
  }
  if (options.apiAuthorizationHeader) {
    apiAuthHeaderStore.setApiAuthHeader(options.apiAuthorizationHeader);
    setApiAuthorizationHeader(options.apiAuthorizationHeader);
  }

  if (options.windowBar) {
    topBarStore.setWindowBar(options.windowBar);
  }

  if (options.theme) {
    if (options.theme.preset === "light") {
      generalStore.light = true;
    }
  } else {
    options.theme = {
      preset: generalStore.light ? "light" : "dark",
      colors: {},
    };
  }

  if (options.ecm) {
    ecmStore.setEcm(options.ecm);
  }

  if (options.htmlMinified === true) {
    generalStore.htmlMinified = true;
  }

  if (options.htmlDesktopFirst === true) {
    generalStore.htmlDesktopFirst = true;
  }

  if (options.googleApiKey) {
    apiKeyStore.setGoogleFontsApiKey(options.googleApiKey);
  }

  if (typeof options.title !== "undefined") {
    generalStore.title = options.title;
  }

  if (options.renameTemplate !== undefined) {
    generalStore.renameTemplate = options.renameTemplate;
  }

  if (options.colors) {
    colorsStore.setColors(options.colors);
  }

  if (options.mobileFirstEnabled !== undefined) {
    mjmlStore.isMobileFirstEnabled = options.mobileFirstEnabled;
  }

  if (options.imageMaxSize !== undefined) {
    fileManagerStore.setMaxImageSize(options.imageMaxSize);
  }

  if (options.imageEditor !== undefined) {
    imageEditorStore.enabled = options.imageEditor;
  }

  if (options.imageEditorOptions) {
    imageEditorStore.options = options.imageEditorOptions;
  }

  if (options.autosaveInterval !== undefined) {
    autosavesStore.setAutosaveInterval(options.autosaveInterval);
  }

  if (options.betaFeatures) {
    betaFeaturesStore.setBetaFeatures(options.betaFeatures);
  }

  if (options.customBlocks) {
    customBlocksStore.setCustomBlocks(options.customBlocks);
  }

  if (options.googlePromotionalAnnotation !== undefined) {
    googlePromotionalStore.setGooglePromotional(
      options.googlePromotionalAnnotation
    );
  }

  if (options.disableAiAssistant) {
    openAiStore.openAiDisabledInOptions = true;
  }

  if (options.premadeTemplates !== undefined) {
    premadeTemplatesStore.setPremadeTemplates(options.premadeTemplates);
  }

  if (options.premadeTemplatesOptions) {
    premadeTemplatesStore.setPremadeTemplatesOptions(
      options.premadeTemplatesOptions
    );
  }

  if (options.chatAI !== undefined) {
    const enableChatAI = withExpansionAndUp("Chat AI", () =>
      chatAIStore.enableChatAI(options.chatAI!, options.chatAIOnSegment!)
    );
    enableChatAI();
  }

  setTheme(options.theme);

  if (options.enableComments !== undefined) {
    const setEnableComments = withBusinessAndUp("Comments", () =>
      commentsStore.setEnableComments(options.enableComments!)
    );
    setEnableComments();
  }

  if (options.customBlocks) {
    const setCustomBlocks = withBusinessAndUp("Custom Blocks", () =>
      customBlocksStore.setCustomBlocks(options.customBlocks!)
    );
    setCustomBlocks();
  }

  if (options.apiBlocks) {
    const setApiBlocks = withBusinessAndUp("API Blocks", () =>
      apiBlocksStore.setCustomBlocks(options.apiBlocks!)
    );
    setApiBlocks();
  }

  if (options.customFileManager) {
    const setCustomFileManager = withBusinessAndUp("Custom File Manager", () =>
      fileManagerStore.setCustomFileManager(true)
    );
    setCustomFileManager();
  }

  if (options.enableFileManagerInGif !== undefined) {
    const setEnableFileManagerInGif = withBusinessAndUp(
      "File Manager in GIF",
      () =>
        fileManagerStore.setEnableFileManagerInGif(
          options.enableFileManagerInGif!
        )
    );
    setEnableFileManagerInGif();
  }

  if (options.role) {
    const setRole = withBusinessAndUp("Roles", () =>
      rulesStore.setRole(options.role!, options.permissions)
    );
    setRole();
  }

  if (options.enableSectionVariants !== undefined) {
    const setEnableSectionVariants = withBusinessAndUp("Section Variants", () =>
      sectionVariantsStore.setEnableSectionVariants(
        options.enableSectionVariants!,
        options.logicalOperatorsInsideVariant,
        options.emailServiceProvider,
        options.disableExtendedComparators
      )
    );
    setEnableSectionVariants();
  }

  if (options.languageMergeTag) {
    mergeTagsStore.setLanguageMergeTagString(options.languageMergeTag);
    mjmlStore.syncLanguageMergeTag();
  }

  if (options.enableImageToTextRatio !== undefined) {
    previewStore.setEnableImageToTextRatio(options.enableImageToTextRatio);
  }
  if (options.enableTemplateSizeInfo !== undefined) {
    previewStore.setEnableTemplateSizeInfo(options.enableTemplateSizeInfo);
  }
  if (options.enableDarkMode !== undefined) {
    previewStore.enableDarkMode = options.enableDarkMode;
  }
  if (options.emailServiceProvider) {
    generalStore.emailServiceProvider = options.emailServiceProvider;
  }

  if (options.helpdesk) {
    helpdeskStore.helpdesk = options.helpdesk;
  }
  if (options.fileManagerPreferences !== undefined) {
    fileManagerStore.setPreferences(options.fileManagerPreferences);
  }
  if (options.imageCompressionOptions) {
    if (
      options.imageCompressionOptions.qualityJpeg !== undefined &&
      options.imageCompressionOptions.qualityJpeg > 0 &&
      options.imageCompressionOptions.qualityJpeg <= 1
    ) {
      fileManagerStore.imageQualityJpeg =
        options.imageCompressionOptions.qualityJpeg;
    }

    if (
      options.imageCompressionOptions.qualityPng !== undefined &&
      options.imageCompressionOptions.qualityPng > 0 &&
      options.imageCompressionOptions.qualityPng <= 1
    ) {
      fileManagerStore.imageQualityPng =
        options.imageCompressionOptions.qualityPng;
    }

    if (options.imageCompressionOptions.enableAutoResize !== undefined) {
      fileManagerStore.enableAutoResize =
        options.imageCompressionOptions.enableAutoResize;
    }

    if (options.imageCompressionOptions.enableCompression !== undefined) {
      fileManagerStore.enableImageCompression =
        options.imageCompressionOptions.enableCompression;
    }
  }

  if (options.multilingual !== undefined) {
    generalStore.multilingual = options.multilingual;
  }

  if (options.customLanguagePreset) {
    generalStore.setCustomLanguagePreset(options.customLanguagePreset);
  }

  if (options.productMergetagTags) {
    apiBlocksStore.setProductMergetagTags(options.productMergetagTags);
  }

  if (options.testingEmails !== undefined && options.testingEmails !== false) {
    if (Array.isArray(options.testingEmails)) {
      previewStore.testingEmailAddresses = options.testingEmails.slice(0, 5);
      localStorage.removeItem("testingEmailAddresses");
      previewStore.testingAddressesLocalStorage = false;
    } else if (localStorage.getItem("testingEmailAddresses")) {
      previewStore.testingEmailAddresses = JSON.parse(
        localStorage.getItem("testingEmailAddresses") as string
      );
    } else {
      previewStore.testingEmailAddresses = [];
      localStorage.setItem("testingEmailAddresses", "[]");
    }
  }

  if (options.defaultTemplateSettings) {
    const defSet = options.defaultTemplateSettings;

    const newDefaultTemplate = applyDefaultTemplateSettings(defSet);

    if (defSet.langs) {
      const { data, success } = DefaultTemplateLangsSchema.safeParse(
        defSet.langs
      );
      if (success) {
        if (!options.multilingual) {
          generalStore.multilingual = true;
        }
        newDefaultTemplate.langs = [];
        const langsInTemplate: string[] = [];
        let primaryIsSet = false;
        for (const lang of data) {
          if (langsInTemplate.includes(lang.key)) {
            continue;
          }
          if (lang.primary && !primaryIsSet) {
            newDefaultTemplate.langs.push({
              ...lang,
              translations: {},
            });
            primaryIsSet = true;
            mjmlStore.langToShow = lang.key;
          } else if (lang.primary && primaryIsSet) {
            newDefaultTemplate.langs.push({
              ...lang,
              translations: {},
            });
          } else {
            newDefaultTemplate.langs.push({
              ...lang,
              translations: {},
            });
          }
          langsInTemplate.push(lang.key);
        }
        if (!primaryIsSet && newDefaultTemplate.langs.length > 0) {
          newDefaultTemplate.langs[0].primary = true;
          mjmlStore.langToShow = newDefaultTemplate.langs[0].key;
        }
      }
    }

    // Update store (same pattern as LPE)
    if (initial) {
      mjmlStore.mjml = JSON.parse(JSON.stringify(newDefaultTemplate));
    }
    mjmlStore.defaultTemplate = newDefaultTemplate;

    if (mode === "testing") {
      //@ts-expect-error
      window.defaultTemplate = newDefaultTemplate;
    }
  }

  if (options.showUnsavedDialogBeforeExit !== false) {
    window.addEventListener("beforeunload", (e) => {
      if (mjmlStore.getEdittedWithoutSave) {
        // This is mandatory for it to work
        e.preventDefault();

        // Doesn't really matter what we return since it's
        // ignored by the modern browsers anyway

        // returnValue needs to be here from Chrome to work (:
        e.returnValue = "";
      }
    });
  }

  //load all set fonts
  fontStore.fonts.forEach((font) => {
    if (font.url !== undefined) {
      loadStyle(font.url);
    }
  });
};

export default setInitState;

export type IOptions = IPluginOptionsPartial;
